import React, { useState, useEffect } from'react'
import { Link, useNavigate } from'react-router-dom'
import { ArrowLeft, Loader2, CheckCircle2, Eye, EyeOff, AlertCircle, ShieldCheck, ExternalLink } from'lucide-react'
import { Button } from'../components/ui/Button'
import { useBranding } from'../hooks/useBranding'
import { BRAND } from '../config/brand'

interface AuthInfo {
 auth_type:'local' |'oauth'
 oauth_provider: string | null
 has_local_password: boolean
 can_change_password: boolean
 email: string
 username: string
}

const OAUTH_PROVIDER_INFO: Record<string, { name: string; settingsUrl: string; icon: string }> = {
 google: {
 name:'Google',
 settingsUrl:'https://myaccount.google.com/security',
 icon:'🔵'
 },
 github: {
 name:'GitHub',
 settingsUrl:'https://github.com/settings/security',
 icon:'⚫'
 },
 microsoft: {
 name:'Microsoft',
 settingsUrl:'https://account.microsoft.com/security',
 icon:'🟢'
 },
 apple: {
 name:'Apple',
 settingsUrl:'https://appleid.apple.com/account/manage',
 icon:'⚪'
 }
}

export const ChangePassword: React.FC = () => {
 const navigate = useNavigate()
 const branding = useBranding()

 const [authInfo, setAuthInfo] = useState<AuthInfo | null>(null)
 const [loadingAuthInfo, setLoadingAuthInfo] = useState(true)
 
 const [currentPassword, setCurrentPassword] = useState('')
 const [newPassword, setNewPassword] = useState('')
 const [confirmPassword, setConfirmPassword] = useState('')
 const [showPassword, setShowPassword] = useState(false)
 const [loading, setLoading] = useState(false)
 const [success, setSuccess] = useState(false)
 const [error, setError] = useState('')

 // Fetch auth info on mount
 useEffect(() => {
 const fetchAuthInfo = async () => {
 try {
 const response = await fetch('/api/auth-info', {
 credentials:'include'
 })
 
 if (response.ok) {
 const data = await response.json()
 setAuthInfo(data)
 } else {
 // If not authenticated, redirect to login
 navigate('/login')
 }
 } catch (err) {
 console.error('Failed to fetch auth info:', err)
 } finally {
 setLoadingAuthInfo(false)
 }
 }

 fetchAuthInfo()
 }, [navigate])

 const getPasswordStrength = (pwd: string) => {
if (pwd.length < 6) return { strength: 0, label:'Too short', color:'bg-bad' }
if (pwd.length < 8) return { strength: 1, label:'Weak', color:'bg-warn' }
 
const hasUpper = /[A-Z]/.test(pwd)
const hasLower = /[a-z]/.test(pwd)
const hasNumber = /[0-9]/.test(pwd)
const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
 
const score = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length
 
if (score >= 3 && pwd.length >= 8) return { strength: 3, label:'Strong', color:'bg-ok' }
if (score >= 2) return { strength: 2, label:'Good', color:'bg-warn' }
return { strength: 1, label:'Weak', color:'bg-warn' }
 }

 const passwordStrength = getPasswordStrength(newPassword)

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault()
 setError('')

 if (newPassword !== confirmPassword) {
 setError('Passwords do not match')
 return
 }

 if (newPassword.length < 6) {
 setError('Password must be at least 6 characters')
 return
 }

 setLoading(true)

 try {
 const response = await fetch('/api/change-password', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({ 
 current_password: currentPassword,
 new_password: newPassword,
 confirm_password: confirmPassword
 })
 })

 const data = await response.json()
 
 if (data.success) {
 setSuccess(true)
 setTimeout(() => navigate('/profile?tab=security'), 3000)
 } else {
 setError(data.message ||'Failed to change password')
 }
 } catch (err) {
 setError('An error occurred. Please try again.')
 } finally {
 setLoading(false)
 }
 }

 // Loading state
 if (loadingAuthInfo) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default">
 <Loader2 className="w-8 h-8 animate-spin text-brand" />
 </div>
 )
 }

 // OAuth user - can't change password here
 if (authInfo && authInfo.auth_type ==='oauth' && !authInfo.can_change_password) {
 const providerInfo = authInfo.oauth_provider 
 ? OAUTH_PROVIDER_INFO[authInfo.oauth_provider.toLowerCase()] || { 
 name: authInfo.oauth_provider, 
 settingsUrl:'#', 
 icon:'🔒' 
 }
 : { name:'External Provider', settingsUrl:'#', icon:'🔒' }

 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8 text-center">
 <div className="w-16 h-16 bg-brand-subtle rounded-full flex items-center justify-center mx-auto mb-6">
 <ShieldCheck className="w-8 h-8 text-brand" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-4">
 {providerInfo.icon} {providerInfo.name} Account
 </h1>
 <p className="text-secondary mb-6">
 You signed up using <strong>{providerInfo.name}</strong> authentication. 
 Your password is managed by {providerInfo.name}, not by {BRAND.assistant}.
 </p>
 
 <div className="bg-brand-subtle rounded-xl p-4 mb-6 text-left">
 <h3 className="font-semibold text-brand mb-2">
 To change your password:
 </h3>
 <ol className="text-sm text-brand space-y-2 list-decimal list-inside">
 <li>Visit your {providerInfo.name} account settings</li>
 <li>Navigate to Security or Password section</li>
 <li>Follow the prompts to update your password</li>
 <li>Your changes will automatically apply to {BRAND.assistant}</li>
 </ol>
 </div>
 
 <div className="space-y-3">
 {providerInfo.settingsUrl !=='#' && (
 <a
 href={providerInfo.settingsUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-brand text-on-brand font-semibold rounded-xl hover:bg-brand-hover transition-all"
 >
 Go to {providerInfo.name} Security Settings
 <ExternalLink className="w-4 h-4" />
 </a>
 )}
 <Link to="/profile">
 <Button variant="outline" className="w-full">
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Profile
 </Button>
 </Link>
 </div>
 
 <p className="mt-6 text-xs text-muted">
 Your account email: <strong>{authInfo.email}</strong>
 </p>
 </div>
 </div>
 </div>
 )
 }

 // Success state
 if (success) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8 text-center">
 <div className="w-16 h-16 bg-ok/10 rounded-full flex items-center justify-center mx-auto mb-6">
 <CheckCircle2 className="w-8 h-8 text-ok" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-4">
 All done!
 </h1>
 <p className="text-secondary mb-6">
 Your password has been updated. Redirecting you back to your profile...
 </p>
 <Link to="/profile?tab=security">
 <Button variant="outline" className="w-full">
 Go to Profile
 </Button>
 </Link>
 </div>
 </div>
 </div>
 )
 }

 // Change password form
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8">
 {/* Header */}
 <div className="text-center mb-8">
 <img src={branding.icon} alt={branding.name} className="w-16 h-16 mx-auto mb-4" />
 <h1 className="text-xl font-semibold text-primary">
 Update Your Password
 </h1>
 <p className="text-secondary mt-2">
 Keep your account secure with a new password.
 </p>
 </div>

 {/* Form */}
 <form onSubmit={handleSubmit} className="space-y-6">
 {error && (
 <div className="p-4 bg-bad/10 border border-bad/30 rounded-lg text-bad text-sm flex items-start gap-2">
 <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
 <span>{error}</span>
 </div>
 )}

 <div>
 <label className="block text-sm font-medium text-secondary mb-2">
 Current Password
 </label>
 <div className="relative">
 <input
 type={showPassword ?'text' :'password'}
 value={currentPassword}
 onChange={(e) => setCurrentPassword(e.target.value)}
 required
 autoComplete="current-password"
 className="w-full px-4 py-3 pr-12 border border-soft rounded-xl bg-surface text-primary focus:ring-2 focus:ring-brand focus:border-transparent transition-all"
 placeholder="Enter your current password"
 />
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-secondary mb-2">
 New Password
 </label>
 <div className="relative">
 <input
 type={showPassword ?'text' :'password'}
 value={newPassword}
 onChange={(e) => setNewPassword(e.target.value)}
 required
 autoComplete="new-password"
 className="w-full px-4 py-3 pr-12 border border-soft rounded-xl bg-surface text-primary focus:ring-2 focus:ring-brand focus:border-transparent transition-all"
 placeholder="Enter new password"
 />
 <button
 type="button"
 onClick={() => setShowPassword(!showPassword)}
 className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-secondary"
 >
 {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
 </button>
 </div>
 
 {/* Password strength indicator */}
 {newPassword && (
 <div className="mt-2">
 <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-soft rounded-full overflow-hidden">
 <div 
 className={`h-full ${passwordStrength.color} transition-all`}
 style={{ width: `${(passwordStrength.strength / 3) * 100}%` }}
 />
 </div>
 <span className="text-xs text-muted">
 {passwordStrength.label}
 </span>
 </div>
 <p className="text-xs text-muted mt-1">
 Use 8+ characters with uppercase, lowercase, numbers & symbols
 </p>
 </div>
 )}
 </div>

 <div>
 <label className="block text-sm font-medium text-secondary mb-2">
 Confirm New Password
 </label>
 <input
 type={showPassword ?'text' :'password'}
 value={confirmPassword}
 onChange={(e) => setConfirmPassword(e.target.value)}
 required
 autoComplete="new-password"
 className={`w-full px-4 py-3 border rounded-xl bg-surface text-primary focus:ring-2 focus:ring-brand focus:border-transparent transition-all ${
confirmPassword && confirmPassword !== newPassword
?'border-bad'
 :'border-soft'
 }`}
 placeholder="Confirm new password"
 />
 {confirmPassword && confirmPassword !== newPassword && (
 <p className="mt-1 text-xs text-bad">Passwords don't match</p>
 )}
 </div>

 <Button
 type="submit"
 disabled={loading || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
 className="w-full h-12 bg-brand hover:bg-brand-hover text-on-brand font-semibold rounded-xl"
 >
 {loading ? (
 <Loader2 className="w-5 h-5 animate-spin" />
 ) : (
'Update Password'
 )}
 </Button>
 </form>

 {/* Back to profile */}
 <div className="mt-6 text-center">
 <Link 
 to="/profile?tab=security" 
 className="inline-flex items-center text-sm text-brand hover:underline"
 >
 <ArrowLeft className="w-4 h-4 mr-1" />
 Back to Profile
 </Link>
 </div>
 </div>
 </div>
 </div>
 )
}

export default ChangePassword
